import { useCallback, useEffect } from 'react';
import { DropdownItem } from 'reactstrap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as moment from 'moment';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes, ChargeTypeResourceTypes } from '../Common';
import { Modal } from '../common/Modal';
import { ResourcePanel } from '../projectManager/ResourcePanel';
import { DatePicker } from '../common/forms/DatePicker';
import { YesNoButtons } from '../common/forms/YesNoButtons';
import { TipAlert } from '../common/TipAlert';
import CustomCircularProgress from '../common/CustomCircularProgress';
import { toasty } from '../common/forms/FormElements';

const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const emptyDayColumns = {
    sunday: 0,
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
};

const clearedDayLockColumns = dayNames.reduce(
    (acc, d) => ({ ...acc, [`${d}HasTimesheet`]: false, [`${d}HasAssignment`]: false }),
    {},
);

const jobResourcesQuery = (jobId) => ({
    queryKey: ['jobShiftResources', jobId],
    queryFn: () => httpClient.js(ApiRoutes.job.byId(jobId)).then((response) => response?.job ?? response),
});

// Days a resource is actually requested on, excluding shift rows and planning-only rows —
// mirrors the server's rule for which days have an original (parent) timesheet.
const getRequestedDayNames = (resources) => dayNames.filter(
    (d) => (resources ?? []).some((r) => !r.newShiftDate && !r.isPlanningOnly && r[d] > 0),
);

const getSelectableResources = (job) => (job?.resources ?? []).filter(
    (r) => !!r.id
        && !r.isPlanningOnly
        && (r.jobResourceTypeId === ChargeTypeResourceTypes.Employee || r.jobResourceTypeId === ChargeTypeResourceTypes.Equipment)
        // Only resources actually requested on this job — skip contract-catalog rows sitting at zero.
        && dayNames.some((d) => r[d] > 0),
);

const buildDraftResources = (job) => {
    const seenTypes = new Set();

    return getSelectableResources(job)
        .filter((r) => {
            const typeKey = [
                r.jobResourceTypeId,
                r.equipmentTypeId ?? '',
                r.applicationGroupId ?? '',
                r.applicationUserGroupId ?? '',
                r.certificationId ?? '',
            ].join('|');

            if (seenTypes.has(typeKey)) {
                return false;
            }
            seenTypes.add(typeKey);
            return true;
        })
        .map((r) => ({
            ...r,
            ...emptyDayColumns,
            ...clearedDayLockColumns,
            isNew: true,
            resourceName: r.resourceName || r.applicationGroupName || r.applicationUserGroupName || r.equipmentTypeName,
        }));
};

const getDayNamesFromDates = (dates, requestedDays) => (dates ?? [])
    .map((d) => dayNames[(moment(d).day() + 6) % 7])
    .filter((d) => requestedDays.includes(d));

const AddShiftResourcesModalBody = ({
    jobId, selectedDates, values, setFieldValue,
}) => {
    const jobQuery = useQuery(jobResourcesQuery(jobId));
    const job = jobQuery.data;
    const selectedDayName = values?.dayName;

    // Once the job loads, preselect the board's selected day (or the first day the job runs)
    // and seed the draft grid. Only when not set yet — a cache update mid-edit must not
    // stomp the user's choices.
    useEffect(() => {
        if (!jobQuery.isSuccess) {
            return;
        }

        if (!selectedDayName) {
            const requestedDays = getRequestedDayNames(job?.resources);
            const daysFromBoard = getDayNamesFromDates(selectedDates, requestedDays);
            setFieldValue('dayName', daysFromBoard[0] ?? requestedDays[0] ?? null);
        }

        if (!values?.shiftResources) {
            setFieldValue('shiftResources', buildDraftResources(job));
        }
    }, [jobQuery.isSuccess, job, selectedDates, setFieldValue, selectedDayName, values?.shiftResources]);

    const jobDayDate = job && values?.dayName
        ? moment(job.weekOfDate).add(dayNames.indexOf(values.dayName), 'days')
        : null;

    if (jobQuery.isLoading || !values?.shiftResources || !jobDayDate) {
        return (
            <div className="d-flex justify-content-center py-4">
                <CustomCircularProgress />
            </div>
        );
    }

    if (jobQuery.isError) {
        return <div className="text-danger">Failed to load job resources.</div>;
    }

    return (
        <>
            <ResourcePanel
                job={{ ...job, resources: values.shiftResources }}
                selectedDate={jobDayDate.toDate()}
                edit={{ allowResources: true, resourceErrors: {} }}
                errors={{}}
                onJobChange={(updatedJob) => setFieldValue('shiftResources', updatedJob.resources)}
            />

            <YesNoButtons
                id="isNewShift"
                name="isNewShift"
                label="This is a new shift"
            />

            {!!values?.isNewShift && (
                <>
                    <TipAlert
                        containerClassName="mt-2"
                        label="A linked timesheet will be created for the new shift; its charges are entered against the shift start date."
                    />

                    <DatePicker
                        id="shiftDate"
                        name="shiftDate"
                        label="Shift Start"
                        required
                        showTimeSelect
                        timeIntervals={15}
                        dateFormat="MM/dd/yyyy h:mm aa"
                        minDate={jobDayDate ? jobDayDate.toDate() : null}
                        error="A shift start date and time are required."
                    />
                </>
            )}
        </>
    );
};

const useAddShiftResourcesModal = ({ jobId, selectedDates }) => {
    const queryClient = useQueryClient();

    const saveResources = useMutation({
        mutationFn: async (values) => {
            const { dayName } = values;
            const requested = (values.shiftResources ?? []).filter((r) => (parseInt(r[dayName], 10) || 0) > 0);

            // The save button is live while the body is still loading; nothing is
            // editable until the job query has resolved.
            if (!dayName || !requested.length) {
                throw new Error('Enter a quantity for at least one resource.');
            }

            if (values.isNewShift && !values.shiftDate) {
                throw new Error('A shift start date and time are required.');
            }

            const { queryKey } = jobResourcesQuery(jobId);
            const job = queryClient.getQueryData(queryKey) ?? await queryClient.fetchQuery(jobResourcesQuery(jobId));
            const selectable = getSelectableResources(job);

            const newRows = requested.map((draft) => {
                const source = selectable.find((r) => r.id === draft.id);

                if (!source) {
                    throw new Error(`"${draft.resourceName}" is no longer on this job.`);
                }

                return {
                    ...source,
                    ...emptyDayColumns,
                    id: 0,
                    [dayName]: parseInt(draft[dayName], 10),
                    newShiftDate: values.isNewShift ? moment(values.shiftDate).format() : null,
                };
            });

            return httpClient.put(ApiRoutes.job.update(job.id), { ...job, resources: [...job.resources, ...newRows] });
        },
        onSuccess: (data, values) => {
            toasty.success(
                values.isNewShift
                    ? 'Resources added. A linked timesheet was created for the new shift.'
                    : 'Resources added.',
            );
        },
        onError: (error) => {
            // Server rejections arrive as HandledError/ValidationError with an empty
            // Error.message — the real text lives on handledError/validationMessages.
            const message = error?.handledError?.message
                || Object.values(error?.validationMessages ?? {})[0]
                || error?.message
                || 'Please try again.';
            toasty.error('Error adding job resources.', message);
        },
    });

    return useCallback(() => {
        Modal.show({
            title: 'Add Shift Resources',
            onSubmit: saveResources.mutateAsync,
            initialValues: { isNewShift: true },
            SaveButtonProps: { savingLabel: 'Saving...' },
            body: (bodyProps) => (
                <AddShiftResourcesModalBody
                    {...bodyProps}
                    jobId={jobId}
                    selectedDates={selectedDates}
                />
            ),
        });
    }, [jobId, selectedDates, saveResources.mutateAsync]);
};

export const AddShiftResources = ({ jobId, selectedDates }) => {
    const showModal = useAddShiftResourcesModal({ jobId, selectedDates });

    return (
        <DropdownItem onClick={showModal}>
            Add Shift Resources
        </DropdownItem>
    );
};

export default AddShiftResources;
